import React, { useCallback, useEffect, useMemo } from "react";
import { Label, Typography, Select } from "@commonsku/styles";

import {
  OptionProps,
  SingleValue,
  components,
  GroupHeadingProps,
} from "react-select";
import { TemplateOption, GroupedOption } from "./types";
import styled from "styled-components";
import { PublicViewTemplate } from "../../../../interfaces/PublicViewTemplate";
import { usePublicViewTemplates } from "../../../admin-new/hooks/usePublicViewTemplates";
import { SelectOption } from "./SelectOption";

const GroupHeadingContainer = styled.div`
  &&& {
    padding: 8px;
  }
  &&& p {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 0;
    text-transform: none;
  }
`;

const GroupHeading = (props: GroupHeadingProps<TemplateOption>) => {
  const { selectProps } = props;
  const { inputValue } = selectProps;

  return inputValue ? null : (
    <GroupHeadingContainer>
      <components.GroupHeading {...props}>
        <Typography.Text>{props.data.label}</Typography.Text>
      </components.GroupHeading>
    </GroupHeadingContainer>
  );
};

const SelectOptionComponentContainer = styled.div`
  display: flex;
  padding: 8px 8px 8px 16px;
`;
const SelectOptionComponent: React.FC<{ label: string }> = ({ label }) => (
  <SelectOptionComponentContainer>{label}</SelectOptionComponentContainer>
);

interface PresentationTemplateSelectProps {
  onUpdateField: (field: string, value: string) => void;
}

const PresentationTemplateSelect = React.memo(
  ({ onUpdateField }: PresentationTemplateSelectProps) => {
    const [currentValue, setCurrentValue] =
      React.useState<TemplateOption | null>(null);

    const {
      templates: fetchedTemplates,
      layouts: fetchedLayouts,
      commonskuTemplates: fetchedCommonskuTemplates,
      companyName: fetchedCompanyName,
    } = usePublicViewTemplates();

    const { layoutOptions, templateOptions, commonskuTemplateOptions } =
      useMemo(() => {
        const fetchedLayoutOptions = fetchedLayouts.map(
          (layout: PublicViewTemplate) => ({
            value: layout.public_view_template_id,
            label: layout.public_view_template_title,
          })
        );

        const fetchedTemplateOptions = fetchedTemplates.map(
          (template: PublicViewTemplate) => ({
            value: template.public_view_template_id,
            label: template.public_view_template_title,
          })
        );

        const fetchedCommonskuTemplateOptions = fetchedCommonskuTemplates.map(
          (template: PublicViewTemplate) => ({
            value: template.public_view_template_id,
            label: template.public_view_template_title,
          })
        );

        return {
          layoutOptions: fetchedLayoutOptions,
          templateOptions: fetchedTemplateOptions,
          commonskuTemplateOptions: fetchedCommonskuTemplateOptions,
        };
      }, [fetchedLayouts, fetchedTemplates, fetchedCommonskuTemplates]);

    const groupedOptions: GroupedOption[] = useMemo(() => {
      return [
        {
          label: `${fetchedCompanyName} Layouts`,
          options: templateOptions,
        },
        {
          label: "commonsku Templates",
          options: commonskuTemplateOptions,
        },
        {
          label: "Layouts",
          options: layoutOptions,
        },
      ];
    }, [
      fetchedCompanyName,
      templateOptions,
      commonskuTemplateOptions,
      layoutOptions,
    ]);

    const handleSelectChange = useCallback(
      (option: SingleValue<TemplateOption>) => {
        const isLayout = layoutOptions.some((l) => l.value === option.value);

        // Set the public_view_layout_id or public_view_template_id based on the selection
        onUpdateField(
          isLayout ? "public_view_layout_id" : "public_view_template_id",
          option.value
        );

        // Clear the other field based on the selection
        onUpdateField(
          isLayout ? "public_view_template_id" : "public_view_layout_id",
          ""
        );

        setCurrentValue(option);
      },
      [layoutOptions, onUpdateField]
    );

    useEffect(() => {
      if (
        currentValue === null &&
        (templateOptions.length || layoutOptions.length)
      ) {
        const value =
          templateOptions[0] ||
          layoutOptions.find((layout) => layout.label === "Gallery");
        handleSelectChange(value);
      }
    }, [currentValue, templateOptions, layoutOptions, handleSelectChange]);

    return (
      <>
        <Label>Templates</Label>
        <Select
          value={currentValue}
          containerStyles={{ width: "100%" }}
          options={groupedOptions}
          onChange={handleSelectChange}
          inPopup
          components={{
            GroupHeading,
            Option: (props: OptionProps<TemplateOption>) => (
              <SelectOption
                {...props}
                children={<SelectOptionComponent label={props.data.label} />}
              />
            ),
          }}
          isMulti={false}
          singleValueStyles={{
            display: "flex",
            alignItems: "center",
          }}
        />
      </>
    );
  }
);

export default PresentationTemplateSelect;
